import React, { useMemo, useState } from 'react';
import { Text, Label, H4 } from '@amzn/stencil-react-components/text';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Spacer, Hr } from '@amzn/stencil-react-components/layout';
import { LineChart, LineType, DataPointShape, Color, LineDataSet } from '@amzn/stencil-react-components/data-visualization';
import { NUM_SPPR_PERIODS, CHART_SPPR_PERIODS } from '../constants/productivityConfiguration';
import moment from 'moment-timezone';
import { Select } from '@amzn/stencil-react-components/form';
import { useUserDataContext, useGlobalContext } from '../hooks';
import { WeeklySpprSummary, Productivity } from '../types/backendService';
import { WeeklyWorkItemData, WeeklyWorkItemDataGroup } from '../types/productivity';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { Card } from '@amzn/stencil-react-components/card';
import { Table } from '@amzn/stencil-react-components/table';
import { LabeledEntry } from '../types/general';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

export const SPPRSummaryTab = ({}) => {
  const { t } = useTranslation();
  const {
    spprSummaries: spprSummariesContext,
    changeSpprWeek,
    spprWeeks: spprWeeksContext,
    selectedSpprWeek,
    selectedWeekProductity,
    showGuardrailRate,
  } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  // Retrieve list of SPPR summaries from the context
  const isSpprSummariesLoading = spprSummariesContext == undefined;
  const spprSummaries: WeeklySpprSummary[] = spprSummariesContext || [];

  // All weeks to display in the week dropdown
  const spprWeeks = spprWeeksContext || [];

  // Metric types to add to the metric dropdown
  const metricTypes: LabeledEntry[] = [
    {
      id: 'units',
      label: t('unitsProcessed'),
    },
    {
      id: 'hours',
      label: t('hoursWorked'),
    },
    {
      id: 'uph',
      label: t('unitsPerHour'),
    },
  ];

  // Selected metric in the metric dropdown
  const [selectedMetric, setSelectedMetric] = useState([metricTypes[2]]);

  /**
   * Chart displaying SPPR data over a series of weeks.
   */
  const spprSummariesChart = useMemo(() => {
    if (spprSummaries.length == 0) {
      return <></>;
    }

    /**
     * We organize the weekly data in a map so we can efficiently look it up within 'tooltipCustomDetails'
     * The data is keyed by the day of the end of the SPPR week.
     */
    type weekData = {x: number, y: number, label: string, units: string, hours: string, rank: string};
    const applicableWeeks: Map<number, weekData> = new Map<number, weekData>();
    for (const item of spprSummaries) {
      if (item.rankPercentile != undefined) {
        const value = {
          x: item.endTime!,
          y: -Math.round(item.rankPercentile),
          label: Math.round(item.rankPercentile).toString(),
          units: item.units!.toString(),
          hours: (item.timeWorked! / 3600000).toFixed(1),
          rank: `${item.rank}/${item.totalPopulation}`,
        }
        applicableWeeks.set(value.x!, value);
      }
    }

    /**
     * We want to display a value on the x axis for each of the last CHART_SPPR_PERIODS weeks
     * even if the user does not have SPPR data for that week.
     */
    const currentMoment = moment().tz(userDetails.locale);
    /**
     * Note: startOf('week') returns a Sunday, so we shift by 3 days to get the wednesday of the last SPPR period
     * that has already ended.
     */
    const lastSpprPeriodEnd = currentMoment.subtract(3, 'days').startOf('week').add(3, 'days');
    let tickValues: number[] = [];
    for (let weekLookback = 0; weekLookback < CHART_SPPR_PERIODS; weekLookback++) {
      tickValues.unshift(lastSpprPeriodEnd.clone().subtract(weekLookback, 'weeks').unix());
    }

    const dataSets: LineDataSet[] = [
      {
        lineType: LineType.Solid,
        dataPointShape: DataPointShape.Circle,
        label: t('percentile'),
        color: Color.Primary,
        data: Array.from(applicableWeeks.values()),
      }
    ]

    return (
      <LineChart
        dataSets={dataSets}
        tooltipCustomDetails={({ x }) => [
          {
            label: t('rank'),
            value: applicableWeeks.get(x as number)!.rank,
          },
          {
            label: t('units'),
            value: applicableWeeks.get(x as number)!.units,
          },
          {
            label: t('hours'),
            value: applicableWeeks.get(x as number)!.hours,
          }
        ]}
        tooltipFormatter={({ x, y }: any) => ({ x: moment(x * 1000).format("M/D/Y"), y: `${-y!}` })}
        height={300}
        shouldFixLabelOverlap={true}
        showBaselineAxis={true}
        showCrosshair={true}
        showDataPoints={true}
        showDependentAxis={true}
        showDependentAxisGridLines={true}
        showLabels={true}
        showLegend={true}
        showTooltip={true}
        baselineAxisTickValues={tickValues}
        dependentAxisTickValues={[-100, -50, 0]}
        baselineAxisFormatter={tick => moment(tick * 1000).format("M/D/Y")}
        dependentAxisFormatter={tick => Math.abs(tick)}
        chartPadding={32}
      />
    );
  }, [spprSummaries]);

  /**
   * Format weekly data so that it can more efficiently/easily be displayed on the UI.
   * Data is built into a map with LC family as the key and a list of work item data points for the week.
   */
  const formatWeeklyData = (weeklyData: Productivity[]): Map<String, WeeklyWorkItemDataGroup[]> => {
    const workItemMap: Map<String, Map<String, WeeklyWorkItemDataGroup>> =
        new Map<String, Map<String, WeeklyWorkItemDataGroup>>();

    const dailyWorkItems = weeklyData;

    /**
     * For a given work item, we have a guardrail rate for each day of the week in the daily data.
     * We need to select a single guardrail rate to display for the overall week for the work item.
     * We select guardrail rate from the latest day with the data in the week and we reverse sort to make this easier.
     */
    dailyWorkItems.sort((a, b) => b.metricTime! - a.metricTime!);

    for (const workItem of dailyWorkItems) {
      const workItemName = [
        workItem.warehouseId,
        workItem.processName,
        workItem.processAttributes!.functionName,
        workItem.processAttributes!.jobAction,
        workItem.processAttributes!.unitType,
        workItem.processAttributes!.sizeCategory,
        workItem.learningCurveId,
      ].join(' / ');
      const dayOfWeek = moment(workItem.metricTime! * 1000).format("ddd"); // 3 char representation (e.g. Mon)
      const workItemMapForLCFamily: Map<String, WeeklyWorkItemDataGroup> =
          workItemMap.get(workItem.learningCurveFamily!) || new Map<String, WeeklyWorkItemDataGroup>();

      const currentWorkItem: WeeklyWorkItemDataGroup = workItemMapForLCFamily.get(workItemName) || {
        units: {
          workItem: workItemName,
          metric: t('unitsProcessed'),
          guardrailRate: '-',
          wed: '',
          thu: '',
          fri: '',
          sat: '',
          sun: '',
          mon: '',
          tue: '',
        },
        hours: {
          workItem: workItemName,
          metric: t('hoursWorked'),
          guardrailRate: '-',
          wed: '',
          thu: '',
          fri: '',
          sat: '',
          sun: '',
          mon: '',
          tue: '',
        },
        uph: {
          workItem: workItemName,
          metric: t('unitsPerHour'),
          guardrailRate: '',
          wed: '',
          thu: '',
          fri: '',
          sat: '',
          sun: '',
          mon: '',
          tue: '',
        },
      };

      const hoursWorked = workItem.timeWorked! / 3600000;
      const uph = (hoursWorked == 0 ? 0 : workItem.units! / hoursWorked).toFixed(1);

      // Cast to 'any' type so we can dynamically set the field based on name
      (currentWorkItem.units as any)[dayOfWeek.toLowerCase()] = workItem.units;
      (currentWorkItem.hours as any)[dayOfWeek.toLowerCase()] = hoursWorked.toFixed(1);
      (currentWorkItem.uph as any)[dayOfWeek.toLowerCase()] = uph;

      if (workItem.configuredRate && currentWorkItem.uph.guardrailRate === '') {
        currentWorkItem.uph.guardrailRate = Math.round(workItem.configuredRate).toString();
      }

      workItemMapForLCFamily.set(workItemName, currentWorkItem);
      workItemMap.set(workItem.learningCurveFamily!, workItemMapForLCFamily);
    }

    const response: Map<String, WeeklyWorkItemDataGroup[]> = new Map<String, WeeklyWorkItemDataGroup[]>();
    for (const lcFamily of Array.from(workItemMap.keys())) {
      const workItemMapForLCFamily = workItemMap.get(lcFamily);
      response.set(lcFamily, Array.from(workItemMapForLCFamily!.values()));
    }

    return response;
  }

  const isWeeklyDataLoading = selectedWeekProductity == undefined;
  const weeklyData = useMemo(() => {
    return formatWeeklyData(selectedWeekProductity || []);
  }, [selectedWeekProductity]);

  // Any time the user selects a new metric update the filtered weekly data
  const filteredWeeklyData = useMemo(() => {
    const newFilteredWeeklyData = new Map<String, WeeklyWorkItemData[]>();

    for (const lcFamily of Array.from(weeklyData.keys())) {
      let filteredData: WeeklyWorkItemData[] = [];

      for (const dataGroup of weeklyData.get(lcFamily)!) {
        for (const metric of selectedMetric) {
          filteredData.push((dataGroup as any)[metric.id]);
        }
      }
      newFilteredWeeklyData.set(lcFamily, filteredData);
    }

    return newFilteredWeeklyData;
  }, [weeklyData, selectedMetric]);

  // Cards for the weekly data. A separate card is created for each LC family.
  const weeklyDataTables = useMemo(() => {
    const weeklyDataTableColumns = showGuardrailRate ? [
      { header: t('workItem'), accessor: 'workItem', width: '41%' },
      { header: t('metric'), accessor: 'metric', width: '10%' },
      { header: t('75thPercentile'), accessor: 'guardrailRate', width: '7%' },
      { header: t('wednesdayShort'), accessor: 'wed', width: '6%' },
      { header: t('thursdayShort'), accessor: 'thu', width: '6%' },
      { header: t('fridayShort'), accessor: 'fri', width: '6%' },
      { header: t('saturdayShort'), accessor: 'sat', width: '6%' },
      { header: t('sundayShort'), accessor: 'sun', width: '6%' },
      { header: t('mondayShort'), accessor: 'mon', width: '6%' },
      { header: t('tuesdayShort'), accessor: 'tue', width: '6%' },
    ] : [
      { header: t('workItem'), accessor: 'workItem', width: '41%' },
      { header: t('metric'), accessor: 'metric', width: '10%' },
      { header: t('wednesdayShort'), accessor: 'wed', width: '7%' },
      { header: t('thursdayShort'), accessor: 'thu', width: '7%' },
      { header: t('fridayShort'), accessor: 'fri', width: '7%' },
      { header: t('saturdayShort'), accessor: 'sat', width: '7%' },
      { header: t('sundayShort'), accessor: 'sun', width: '7%' },
      { header: t('mondayShort'), accessor: 'mon', width: '7%' },
      { header: t('tuesdayShort'), accessor: 'tue', width: '7%' },
    ];

    let cards = [];
    const lcFamilies = Array.from(filteredWeeklyData.keys());

    let cardIndex = 0;
    for (const lcFamily of lcFamilies) {
      cards.push(
        <Card key={cardIndex++}>
          <Col width="100%">
            <H4 id={`{lcFamily}-table-label`}>{lcFamily}</H4>
            <Spacer height={8}/>
            <Table
              aria-labelledby={`{lcFamily}-table-label`}
              columns={weeklyDataTableColumns}
              data={filteredWeeklyData.get(lcFamily)!}
            />
          </Col>
        </Card>
      )
    }

    return (
      <Col gridGap="S300">
        {cards}
      </Col>
    )
  }, [filteredWeeklyData, showGuardrailRate]);

  // Logic to add loading spinners when appropriate
  let weeklyDataTablesContent;
  if (isWeeklyDataLoading) {
    weeklyDataTablesContent = (
      <Flex flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText loadingText={t('loadingSpprData')} />
      </Flex>
    );
  } else if (weeklyData.size == 0) {
    weeklyDataTablesContent = <Text>{t('noSpprDataForWeek')}</Text>
  } else {
    weeklyDataTablesContent = weeklyDataTables;
  }

  let isNoSPPRPercentilePresent = spprSummaries.every((spprSummary) => spprSummary.rankPercentile == undefined);

  if (isSpprSummariesLoading) {
    return (
      <Flex flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText loadingText={t('loadingSpprData')} />
      </Flex>
    );
  } else if (spprSummaries.length == 0) {
    return (
      <Text>{t('noSpprData', { weeks: NUM_SPPR_PERIODS })}</Text>
    );
  } else if (isNoSPPRPercentilePresent) {
    return (
      <Text>{t('noSpprPercentile', { weeks: NUM_SPPR_PERIODS })}</Text>
    );
  } else {
    return (
      <>
        <Text fontWeight="light">
          {t('spprSectionIntro', { periods: NUM_SPPR_PERIODS })}
          <IconWithTooltip tooltipText={t('productivityPercentileExplanation')} />
        </Text>

        <Spacer height={16}/>

        {spprSummariesChart}

        <Spacer height={16}/>
        <Text fontWeight="light">
          {t('spprWeeklyHeading')}
          <IconWithTooltip tooltipText={t('workItemDisclaimer')} />
        </Text>
        <Hr size="wide" />
        <Spacer height={16}/>
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center" gridGap="S300">
          <Flex flexDirection="row" justifyContent="left" alignItems="center" gridGap="S300">
            <Col alignItems="start">
              <Label htmlFor="metric-selector" id="metric-selector-label">
                <Text fontSize="T300" fontWeight="medium" textAlign="right">
                  {t('metric')}
                </Text>
              </Label>
            </Col>
            <Col>
              <Select
                labelId="metric-selector-label"
                id="metric-selector"
                options={metricTypes}
                value={selectedMetric}
                onChange={setSelectedMetric}
                renderOption={(option: LabeledEntry) => option.label}
                valueAccessor={(option: LabeledEntry) => option.id}
                getTriggerText={(count) => (count === 1 ? t('itemSelected') : t('itemsSelected', { count }))}
                isMulti={true}
              />
            </Col>
          </Flex>
          <Flex flexDirection="row" justifyContent="right" alignItems="center" gridGap="S300">
            <Col alignItems="start">
              <Label htmlFor="week-selector" id="week-selector-label">
                <Text fontSize="T300" fontWeight="medium" textAlign="right">
                  {t('weekEnding')}
                </Text>
              </Label>
            </Col>
            <Col width={150}>
              <Select
                labelId="week-selector-label"
                id="week-selector"
                options={spprWeeks}
                value={selectedSpprWeek}
                onChange={changeSpprWeek}
                renderOption={option => option.format("M/D/Y")}
              />
            </Col>
          </Flex>
        </Flex>
        <Spacer height={16}/>

        {weeklyDataTablesContent}
      </>
    );
  }
}
